import React, { useCallback } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { Notification } from 'baseui/notification';
import { Spinner } from 'baseui/spinner';
import { HeadingXSmall, ParagraphSmall } from 'baseui/typography';
import { AnalyticsProvider } from '../../analytics/analytics-provider';
import type { SendEventT } from '../../analytics/types';
import type { NOTIFICATION_TYPE } from '../constant/constant';
import { REVIEW_SUGGESTIONS_EVENT_NAMES } from './analytics/event-name';
import { ActionSectionComponent } from './action-section';
import { ICON_BORDER_WIDTH, ICON_SIZE } from './constants';
import { ReviewSuggestionsCard as ReviewSuggestionsCardsContainer } from './review-suggestions-card';
import { StatesRenderer } from './states-renderer';
import { Container, ContentWrapper, correctionCountOverrides, HeadingContainer, notificationOverrides, StateContainer, StyledCircleCheckIcon, StyledInfoCircleExclamationPointIcon, StyledParagraphMedium, StyledWarningCircleExclamationPointIcon, SuggestionsSection, TitleContainer } from './styled-components-overrides';
import type { onFocusSuggestionFunctionT, PanelStateT, ReviewSuggestionsDetailT, SuggestionActionT, SuggestionDetailT } from './types';
import { SUGGESTION_CATEGORY, SUGGESTION_STATE } from './types';
type PropsT = {
  initialState: ReviewSuggestionsDetailT[];
  onSuggestionAction: (item: ReviewSuggestionsDetailT, action: SuggestionActionT) => Promise<void>;
  sendEvent: SendEventT;
  onEvaluate: () => Promise<void>;
  showNotification?: (notificationType: (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE], message: string) => void;
  onFocusSuggestion?: onFocusSuggestionFunctionT;
  panelState?: PanelStateT;
};
export type SuggestionWithCategoryT = SuggestionDetailT & {
  category: SUGGESTION_CATEGORY;
};
const ReviewSuggestionsSidePanelContent = ({
  initialState,
  onSuggestionAction,
  sendEvent,
  panelState,
  onFocusSuggestion,
  onEvaluate
}: PropsT) => {
  const [, theme] = useStyletron();
  const handleEvaluate = useCallback(() => {
    sendEvent({
      action: REVIEW_SUGGESTIONS_EVENT_NAMES.EVALUATE_TRACKING_DETAILS,
      payload: {
        trigger: 're-run validations'
      }
    });
    return onEvaluate();
  }, [sendEvent, onEvaluate]);
  const allSuggestionsAreResolved = initialState.every(suggestion => suggestion?.state === SUGGESTION_STATE.APPLIED || suggestion?.state === SUGGESTION_STATE.DISMISSED);
  const hasStaleState = panelState?.isStale || initialState.some(suggestion => suggestion?.state === SUGGESTION_STATE.STALE);
  const suggestionCount = initialState.reduce((acc, suggestion) => {
    if ((suggestion.category === SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR || suggestion.category === SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY) && suggestion.state === SUGGESTION_STATE.PENDING) {
      return acc + 1;
    }
    return acc;
  }, 0);
  const isLoadingOrHasError = panelState?.isLoading || panelState?.hasError;
  const isEvaluateDisabled = isLoadingOrHasError || !(allSuggestionsAreResolved || hasStaleState);
  const suggestionVisible = !isLoadingOrHasError && !allSuggestionsAreResolved;
  const contentHeight = suggestionVisible ? '100%' : 'calc(100vh - 200px)';
  const getContent = () => {
    if (panelState?.isLoading) {
      return <StateContainer data-testid="loading-state">
          <StatesRenderer icon={<Spinner $size={ICON_SIZE} $borderWidth={ICON_BORDER_WIDTH} />} title="Evaluating..." //Remove 'answers' to make it generic
        description="Please wait while the system checks your inputs for errors" />
        </StateContainer>;
    }
    if (panelState?.hasError) {
      return <StateContainer data-testid="error-state">
          <StatesRenderer icon={<StyledWarningCircleExclamationPointIcon />} title="Network error" description="The system was unable to run validations. Please try again" footer={<Button kind={KIND.secondary} shape={SHAPE.pill} size={SIZE.compact} onClick={handleEvaluate}>
                Re-run validations
              </Button>} />
        </StateContainer>;
    }
    if (allSuggestionsAreResolved) {
      return <StateContainer data-testid="all-resolved-state">
          <StatesRenderer icon={<StyledCircleCheckIcon />} title="All errors resolved" description="There are no errors for now. You can go ahead and submit the task" />
        </StateContainer>;
    }
    return <SuggestionsSection>
        {hasStaleState && <Notification overrides={notificationOverrides}>
            {() => <>
                <StyledInfoCircleExclamationPointIcon />
                <StyledParagraphMedium width={'80%'}>
                  You have made changes to content. Click evaluate to check for errors again
                </StyledParagraphMedium>
              </>}
          </Notification>}
        <ReviewSuggestionsCardsContainer data={initialState} onSuggestionAction={onSuggestionAction} onFocusSuggestion={onFocusSuggestion} />
      </SuggestionsSection>;
  };
  return <AnalyticsProvider sendEvent={sendEvent}>
      <Container>
        <ContentWrapper $height={contentHeight}>
          <HeadingContainer>
            <TitleContainer>
              <HeadingXSmall margin={0} paddingRight={theme.sizing.scale300}>
                Corrections{' '}
              </HeadingXSmall>
              {initialState.length > 0 && !isLoadingOrHasError && <Button size={SIZE.mini} shape={SHAPE.rounded} overrides={correctionCountOverrides} data-testid="correction-count">
                  {suggestionCount}
                </Button>}
            </TitleContainer>
            <ParagraphSmall margin={0}>Resolve all errors to submit your task</ParagraphSmall>
          </HeadingContainer>
          {getContent()}
        </ContentWrapper>
        <ActionSectionComponent disabled={isEvaluateDisabled} onEvaluate={handleEvaluate} />
      </Container>
    </AnalyticsProvider>;
};
export default ReviewSuggestionsSidePanelContent;